import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChangeTrackingAdminApi, DashboardStats } from '../api/changeTrackingAdmin.api';

// Import icons
import {
  Activity,
  AlertTriangle,
  TrendingUp,
  Server,
  Clock,
  RefreshCw,
  Eye,
  Settings,
  FileText,
  BarChart3
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';


const ChangeTrackingDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Load dashboard data
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const statsData = await ChangeTrackingAdminApi.getDashboardStats();

      setStats(statsData);
      setLastUpdate(new Date());
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Setup auto-refresh
  useEffect(() => {
    loadDashboardData();

    // Auto-refresh every 30 seconds
    const interval = setInterval(loadDashboardData, 30000);

    return () => clearInterval(interval);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-yellow-500';
      case 'low': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };



  if (loading && !stats) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
          <span className="ml-2 text-slate-600">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4">
          <div className="flex items-center">
            <span className="text-red-600 mr-2">⚠️</span>
            <span className="text-red-800">{error}</span>
            <button
              className="ml-2 px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50"
              onClick={loadDashboardData}
            >
              <RefreshCw className="h-4 w-4 inline mr-1" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Change Tracking Dashboard</h1><PageInfoButton /></div>
          <p className="text-slate-600 mt-1">
            Monitor system changes and maintain oversight of all modifications
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-slate-500">
            Last updated: {lastUpdate.toLocaleTimeString()}
          </span>
          <button
            className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50"
            onClick={loadDashboardData}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 inline mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Recent Changes (24h)</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.recentChanges || 0}</p>
              <p className="text-xs text-gray-500">Changes in last 24 hours</p>
            </div>
            <Activity className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Aggregations</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.pendingAggregations || 0}</p>
              <p className="text-xs text-gray-500">Awaiting processing</p>
            </div>
            <Clock className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">System Health</p>
              <p className="text-2xl font-bold text-green-600">Healthy</p>
              <p className="text-xs text-gray-500">All services operational</p>
            </div>
            <Server className="h-8 w-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Monitoring</p>
              <p className="text-2xl font-bold text-blue-600">Real-time</p>
              <p className="text-xs text-gray-500">Auto-refresh enabled</p>
            </div>
            <Eye className="h-8 w-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Main Content Tabs */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex">
            <button className="px-4 py-2 text-sm font-medium text-blue-600 border-b-2 border-blue-600">
              Overview
            </button>
            <button className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
              Recent Changes
            </button>
            <button className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700">
              Analytics
            </button>
          </nav>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Severity Breakdown */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <BarChart3 className="h-5 w-5 mr-2" />
                Change Severity (7 days)
              </h3>
              <div className="space-y-3">
                {stats?.severityBreakdown?.map((item) => (
                  <div key={item.severity} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-2 ${getSeverityColor(item.severity)}`}></div>
                      <span className="capitalize text-sm">{item.severity}</span>
                    </div>
                    <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">{item.count}</span>
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm">No data available</p>
                )}
              </div>
            </div>

            {/* Top Changed Entities */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Most Changed Entities (7 days)
              </h3>
              <div className="space-y-3">
                {stats?.topEntities?.slice(0, 5).map((entity) => (
                  <div key={entity.entity_type} className="flex items-center justify-between">
                    <span className="capitalize text-sm">{entity.entity_type.replace('_', ' ')}</span>
                    <span className="border border-gray-300 text-gray-700 text-xs px-2 py-1 rounded">{entity.count}</span>
                  </div>
                )) || (
                  <p className="text-gray-500 text-sm">No data available</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-2">
          <button
            className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 flex items-center"
            onClick={() => navigate('/admin/change-tracking/history')}
          >
            <FileText className="h-4 w-4 mr-1" />
            View Change History
          </button>
          <button
            className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 flex items-center"
            onClick={() => navigate('/admin/change-tracking/notifications')}
          >
            <AlertTriangle className="h-4 w-4 mr-1" />
            Manage Notifications
          </button>
          <button
            className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 flex items-center"
            onClick={() => navigate('/admin/change-tracking/config')}
          >
            <Settings className="h-4 w-4 mr-1" />
            System Configuration
          </button>
          <button
            className="px-4 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50 flex items-center"
            onClick={() => navigate('/admin/change-tracking/audit')}
          >
            <BarChart3 className="h-4 w-4 mr-1" />
            Audit & Compliance
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeTrackingDashboard;